import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { clamp, isNumber } from 'lodash';
import { useMount } from 'react-use';
import { Box, Typography, Slider } from '@mui/material';
import { getUserSettings } from '@surfline/web-common';

import { MDY } from 'common/constants';
import { useAppSelector } from 'stores/hooks';
import type {
  GraphId,
  GraphScrubbedEvent,
} from 'containers/ForecastDataVisualizer/GraphTooltips/GraphTooltipProvider/GraphTooltipProvider';
import {
  useActiveSpectraLeftPosition,
  useActiveSpectraLeftNowPosition,
  useActiveSpectraTimeLabel,
  useActiveSpectraTimestamp,
} from 'utils/globalGraphState/globalSwellSpectraState';
import { formatTimestampInTimezone } from 'utils/time';

import styles from './ForecastGraphSlider.module.scss';

const NOW_LABEL_WIDTH = 25;
const POSITION_TOLERANCE_THRESHOLD = 1;
const SLIDER_VALUE_UPDATE_THRESHOLD = 0.1;

interface Props {
  customWidth?: number;
  graphWidth: number;
  leftLastDataPointPosition?: number;
  renderTooltip: (xPercentage: number, graphId: GraphId) => void;
  trackGraphScrubbed: GraphScrubbedEvent;
  numberOfDays: number;
  utcOffset: number;
}

const SliderTimeLabel = ({
  utcOffset,
  sliderHandleWidth,
  containerWidth,
}: {
  utcOffset: number;
  containerWidth: number;
  sliderHandleWidth: number;
}) => {
  const timeLabelRef = useRef<HTMLDivElement | null>(null);
  const timeLabelWidth = timeLabelRef?.current?.offsetWidth || 0;

  const settings = useAppSelector(getUserSettings);
  const userDateFormat = settings?.date?.format;
  const dateFormat = userDateFormat === MDY ? 'EEEE M/d hh:mmaaa' : 'EEEE d/M hh:mmaaa';

  const [leftPosition] = useActiveSpectraLeftPosition();
  const [timestamp] = useActiveSpectraTimestamp();
  const [timeLabel] = useActiveSpectraTimeLabel();

  const timeLabelText = useMemo(() => {
    const formattedLabel = formatTimestampInTimezone(timestamp || 0, utcOffset || 0, dateFormat);
    return timeLabel === 'Now' ? 'Now' : formattedLabel;
  }, [dateFormat, timeLabel, timestamp, utcOffset]);

  const getTimeLabelLeftPosition = useCallback(() => {
    if (!isNumber(leftPosition)) return { left: -1 };

    const timeLabelPosition = leftPosition - timeLabelWidth / 2;

    if (timeLabelWidth / 2 >= leftPosition) {
      return { left: 0 };
    }

    if (leftPosition + timeLabelWidth / 2 >= containerWidth) {
      return {
        left: containerWidth - sliderHandleWidth - timeLabelWidth,
      };
    }

    return { left: timeLabelPosition };
  }, [containerWidth, leftPosition, sliderHandleWidth, timeLabelWidth]);

  return (
    <div className={styles.timeLabelContainer}>
      <Typography
        className={styles.timeLabel}
        data-testid="forecast-slider-time-label"
        ref={timeLabelRef}
        style={getTimeLabelLeftPosition()}
        variant="callout1"
      >
        {timeLabelText}
      </Typography>
    </div>
  );
};

const SliderDayDividers = ({
  numberOfDays,
  containerWidth,
}: {
  numberOfDays: number;
  containerWidth: number;
}) => {
  const sliderRailDividersStyle = useCallback(
    (index: number) => ({ left: `${(containerWidth / numberOfDays) * (index + 1) + 7}px` }),
    [containerWidth, numberOfDays],
  );

  const dividers = useMemo(
    () => (
      <>
        {Array(numberOfDays)
          .fill(null)
          .map((day, index) => {
            const isLastDayMidnightTick = index + 1 === numberOfDays;
            return !isLastDayMidnightTick ? (
              <div
                key={`${index + 1}`}
                className={styles.sliderRailDividers}
                data-testid="forecast-graph-slider-day-dividers"
                style={sliderRailDividersStyle(index)}
              />
            ) : null;
          })}
      </>
    ),
    [numberOfDays, sliderRailDividersStyle],
  );

  return dividers;
};

const DayDividers = React.memo(SliderDayDividers);

const ForecastGraphSlider: React.FC<Props> = ({
  customWidth,
  graphWidth,
  leftLastDataPointPosition,
  renderTooltip,
  trackGraphScrubbed,
  numberOfDays,
  utcOffset,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const graphScrubbedTrackedRef = useRef<boolean>(false);
  const [isMounted, setIsMounted] = useState(false);
  const [leftNowPosition] = useActiveSpectraLeftNowPosition();
  const [leftPosition] = useActiveSpectraLeftPosition();
  const [fixedNowPosition, setFixedNowPosition] = useState<number>(-1);
  const [currentSliderValue, setCurrentSliderValue] = useState<number>(0);

  useEffect(() => {
    // reset the tracking flag when the event handler changes as this indicates
    // that we switched days with the day selector or in/out of table view
    graphScrubbedTrackedRef.current = false;
  }, [trackGraphScrubbed]);

  // Capture the "Now" position when we're actually at the "Now" time
  useEffect(() => {
    if (isNumber(leftNowPosition) && leftNowPosition >= 0) {
      // Calculate what the slider value should be for the "Now" position
      const nowSliderValue = (leftNowPosition / graphWidth) * 100;

      // If the current slider position is close to the "Now" position, update the fixed position
      if (Math.abs(currentSliderValue - nowSliderValue) < POSITION_TOLERANCE_THRESHOLD) {
        setFixedNowPosition(leftNowPosition);
      }
    }
  }, [leftNowPosition, currentSliderValue, graphWidth]);

  const checkIfShouldTrackScrubbing = useCallback(() => {
    if (graphScrubbedTrackedRef.current === false) {
      graphScrubbedTrackedRef.current = true;
      trackGraphScrubbed('spectra', {});
    }
  }, [trackGraphScrubbed]);

  // Calculate the maximum draggable percentage based on actual data extent
  const maxDraggablePercentage = useMemo(() => {
    // Use leftLastDataPointPosition as percentage of total width
    if (isNumber(leftLastDataPointPosition) && leftLastDataPointPosition > 0 && graphWidth) {
      return (leftLastDataPointPosition / graphWidth) * 100;
    }

    // Fallback to customWidth if no leftLastDataPointPosition
    if (customWidth && graphWidth) {
      return (customWidth / graphWidth) * 100;
    }

    // Default to 100% if no constraints
    return 100;
  }, [leftLastDataPointPosition, customWidth, graphWidth]);

  // Convert left position to slider value (0-maxDraggablePercentage)
  const sliderValue = useMemo(() => {
    if (!isNumber(leftPosition) || !graphWidth) return 0;

    // Calculate position as percentage of the full graph width
    const percentage = (leftPosition / graphWidth) * 100;
    const clampedPercentage = Math.min(percentage, maxDraggablePercentage);

    // Update current slider value if it's different (for initial load)
    if (Math.abs(currentSliderValue - clampedPercentage) > SLIDER_VALUE_UPDATE_THRESHOLD) {
      setCurrentSliderValue(clampedPercentage);
    }

    return clampedPercentage;
  }, [leftPosition, graphWidth, maxDraggablePercentage, currentSliderValue]);

  const handleSliderChange = useCallback(
    (_event: Event, newValue: number | number[]) => {
      const value = Array.isArray(newValue) ? newValue[0] : newValue;

      // Track the current slider value
      setCurrentSliderValue(value);

      // Prevent dragging beyond the maximum draggable percentage
      if (value > maxDraggablePercentage) {
        return; // Don't update if trying to drag beyond data extent
      }

      // Convert slider value (percentage) to decimal
      const xPercentage = Math.min(value / 100, 0.99);
      renderTooltip(clamp(xPercentage, 0, 1), 'spectra');
      checkIfShouldTrackScrubbing();
    },
    [checkIfShouldTrackScrubbing, renderTooltip, maxDraggablePercentage],
  );

  const sliderNowLabelStyles = useMemo(() => {
    // Use the fixed "Now" position that was captured when not interacting
    const nowPosition = fixedNowPosition >= 0 ? fixedNowPosition : leftNowPosition;

    if (!isNumber(nowPosition) || nowPosition < 0) {
      return { display: 'none' };
    }

    const halfOfLabelWidth = NOW_LABEL_WIDTH / 2;
    const isPositionAtEnd = nowPosition > graphWidth - NOW_LABEL_WIDTH;

    const position = isPositionAtEnd
      ? graphWidth - NOW_LABEL_WIDTH
      : nowPosition - halfOfLabelWidth;

    return {
      display: 'block',
      left: `${position}px`,
      width: `${NOW_LABEL_WIDTH}px`,
    };
  }, [graphWidth, leftNowPosition, fixedNowPosition]);

  const sliderContainerStyles = useMemo(
    () => ({
      width: `${graphWidth}px`,
    }),
    [graphWidth],
  );

  const unavailableDataAreaStyles = useMemo(
    () => ({
      left: `${maxDraggablePercentage}%`,
      width: `${100 - maxDraggablePercentage}%`,
      borderRadius: maxDraggablePercentage > 0 ? '0 2px 2px 0' : '2px',
    }),
    [maxDraggablePercentage],
  );

  const partialUnavailableAreaStyles = useMemo(() => {
    if (
      !customWidth ||
      !leftLastDataPointPosition ||
      !isNumber(leftLastDataPointPosition) ||
      leftLastDataPointPosition <= 0
    ) {
      return null;
    }

    const customWidthPercentage = (customWidth / graphWidth) * 100;
    const actualDataPercentage = (leftLastDataPointPosition / graphWidth) * 100;

    if (actualDataPercentage >= customWidthPercentage) {
      return null;
    }

    return {
      left: `${actualDataPercentage}%`,
      width: `${customWidthPercentage - actualDataPercentage}%`,
    };
  }, [customWidth, leftLastDataPointPosition, graphWidth]);

  useMount(() => {
    if (!isMounted) {
      setIsMounted(true);
    }
  });

  return (
    <div data-testid="forecast-graph-slider">
      <SliderTimeLabel utcOffset={utcOffset} sliderHandleWidth={10} containerWidth={graphWidth} />
      <Box ref={containerRef} className={styles.sliderContainer} style={sliderContainerStyles}>
        <Typography className={styles.nowLabel} style={sliderNowLabelStyles} variant="callout1">
          Now
        </Typography>
        <DayDividers containerWidth={graphWidth} numberOfDays={numberOfDays} />
        <Slider
          value={sliderValue}
          onChange={handleSliderChange}
          min={0}
          max={100}
          step={0.1}
          className={styles.slider}
          data-testid="forecast-graph-slider-handle"
        />
        {/* Show unavailable data area beyond actual data */}
        {maxDraggablePercentage < 100 && (
          <Box
            className={styles.unavailableDataArea}
            style={unavailableDataAreaStyles}
            data-testid="forecast-graph-slider-unavailable-area"
          />
        )}
        {/* Show additional unavailable area between actual data and custom width if applicable */}
        {partialUnavailableAreaStyles && (
          <Box
            className={styles.partialUnavailableArea}
            style={partialUnavailableAreaStyles}
            data-testid="forecast-graph-slider-partial-unavailable-area"
          />
        )}
      </Box>
    </div>
  );
};

export default React.memo(ForecastGraphSlider);
